import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Camera, Check, Pin } from 'lucide-react'
import { api, upload } from '../../lib/api.js'
import useSaveShortcut from '../../lib/useSaveShortcut.js'
import { toast } from '../../components/feedback/Toast.jsx'
import { fmtDay, todayISO, adoptKeyboard, MIC_PRESETS, SAMPLE_RATES, BIT_DEPTHS, SESSION_TYPES, ROOMS } from './lib.js'
import BackBar from './BackBar.jsx'
import Segmented from '../../components/ui/Segmented.jsx'

const TITLE_PLACEHOLDER = 'New session'

/**
 * New session (optionally recalled from a previous one) and edit, one form.
 * The recall prefill happens here on the client: mics, chain and format
 * carry over; the title and notes start fresh — notes are what changed
 * *today*. The title IS the heading: it arrives selected, so typing
 * replaces it.
 */
export default function SessionFormPage() {
  const { projectId, sessionId } = useParams()
  const [params] = useSearchParams()
  const fromId = params.get('from')
  const editing = Boolean(sessionId)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const titleRef = useRef(null)
  const formRef = useRef(null)

  // requestSubmit (not save()) so native validation still runs.
  useSaveShortcut(() => formRef.current?.requestSubmit())

  const source = useQuery({
    queryKey: ['sl-session', sessionId || fromId],
    queryFn: () => api(`/log/sessions/${sessionId || fromId}`),
    enabled: Boolean(sessionId || fromId),
  })

  // Every "Edit goes to" address previously logged — feeds the field's
  // native datalist so a known address autocompletes after a few keys.
  const completions = useQuery({
    queryKey: ['sl-completions'],
    queryFn: () => api('/log/completions'),
    staleTime: 5 * 60 * 1000,
  })
  // The session types: the built-ins plus every one made permanent from an
  // Other box, here or in the Session Builder — one list for the studio
  // (Charles, 2026-10-09). canAdd = may make one permanent (admins).
  const typesQ = useQuery({
    queryKey: ['sl-session-types'],
    queryFn: () => api('/log/session-types'),
    staleTime: 5 * 60 * 1000,
  })

  // Inside a multi-day session the title defaults to the next day number —
  // "Day 3" — still select-all'd, so typing replaces it (Charles, 2026-08-04).
  // The count usually sits in cache from the project page; the query below
  // covers a cold load (direct URL, refresh).
  const project = useQuery({
    queryKey: ['sl-project', projectId],
    queryFn: () => api(`/log/projects/${projectId}`),
    enabled: Boolean(projectId) && !editing,
  })
  const dayTitle = (p) => (p?.sessions ? `Day ${p.sessions.length + 1}` : null)

  // What the form put in the title (vs. what the user typed) — focus
  // re-selects it, and the async day-count only ever replaces it.
  const autoLabel = useRef(TITLE_PLACEHOLDER)

  // ?title= arrives from a calendar suggestion — remember the ORIGINAL
  // booking title separately from the editable label, so the suggestion
  // stays hidden even after the session is renamed (Charles, 2026-08-06).
  const calendarTitle = useRef(editing ? '' : params.get('title') || '')

  // ?label= arrives from a calendar suggestion — the booking's name is
  // almost certainly the right title, so it lands as real text (still
  // select-all'd below, so typing replaces it like the placeholder). It is
  // the booking WITHOUT its "(1)"/"(A)" room marker: the room has its own
  // field below (Charles, 2026-08-29). Older links only carry ?title=.
  const suggestedLabel = params.get('label') || params.get('title')
  const [label, setLabel] = useState(() => {
    if (editing) return ''
    if (suggestedLabel) return suggestedLabel
    const day = dayTitle(qc.getQueryData(['sl-project', projectId]))
    if (day) autoLabel.current = day
    return day || TITLE_PLACEHOLDER
  })
  // `?date=` lets the admin log check open this form for a PAST booking, so
  // a late log lands on the day it happened (Charles, 2026-09-20). Only a
  // well-formed day that isn't in the future is taken; anything else → today.
  const [date, setDate] = useState(() => {
    const asked = editing ? '' : params.get('date') || ''
    return /^\d{4}-\d{2}-\d{2}$/.test(asked) && asked <= todayISO() ? asked : todayISO()
  })
  // ?room= / ?sessionType= arrive from a calendar-suggestion tap — the
  // booking's "(A"/"(1"/"(5" marker and publisher-name → Audiobook
  // (Charles, 2026-08-08). Only known values select; the picker stays live.
  const [room, setRoom] = useState(() => (editing ? null : ROOMS.includes(params.get('room')) ? params.get('room') : null))
  const [sampleRate, setSampleRate] = useState(null)
  const [bitDepth, setBitDepth] = useState(null)
  const [gear, setGear] = useState([])
  const [chain, setChain] = useState('')
  const [notes, setNotes] = useState('')
  // ?editGoesTo= / ?narrators= arrive from a calendar suggestion whose
  // booking body listed them (e.g. "Edit goes to: name@x.com").
  const [editGoesTo, setEditGoesTo] = useState(editing ? '' : params.get('editGoesTo') || '')
  const [narrators, setNarrators] = useState(editing ? '' : params.get('narrators') || '')
  // Each day's own bookmark — where the narrator ended. Deliberately NOT
  // carried on recall: yesterday's stopping point isn't today's.
  const [leftOffPage, setLeftOffPage] = useState('')
  const [sessionType, setSessionType] = useState(() => (editing ? null : SESSION_TYPES.includes(params.get('sessionType')) ? params.get('sessionType') : null))
  const [sessionTypeOther, setSessionTypeOther] = useState('')
  // A session saved with a type that has since left the list keeps it as
  // its own choice, so an edit never quietly drops it.
  const sessionTypes = typesQ.data?.sessionTypes || SESSION_TYPES
  const typeOptions = !sessionType || sessionTypes.includes(sessionType)
    ? sessionTypes
    : [...sessionTypes.filter((t) => t !== 'Other'), sessionType, 'Other']
  // Per-day expense, like leftOffPage — NOT carried on recall.
  const [lunchCost, setLunchCost] = useState('')
  // Social-media flags (Charles, 2026-09-01): blank on most sessions, and
  // filled in only when the work is worth posting about. Both carry forward
  // on recall like the format does — same project, same book, same talent.
  const [socialTitle, setSocialTitle] = useState('')
  const [notableTalent, setNotableTalent] = useState('')
  const [picking, setPicking] = useState(false)
  const [photos, setPhotos] = useState([]) // Files pending upload after create
  const [busy, setBusy] = useState(false)
  const [seeded, setSeeded] = useState(false)
  const photoRef = useRef(null)

  useEffect(() => {
    if (seeded || !source.data?.session) return
    const s = source.data.session
    setGear(s.gear)
    setChain(s.chain.join(' → '))
    // Same project, same room, day after day — the room recalls like the format.
    setRoom(s.room || null)
    setSampleRate(s.sampleRate || null)
    setBitDepth(s.bitDepth || null)
    // Same project, same kind of session — the type recalls like the format.
    setSessionType(s.sessionType || null)
    setSessionTypeOther(s.sessionTypeOther || '')
    if (editing) {
      setLabel(s.label || TITLE_PLACEHOLDER)
      setDate(s.date)
      setNotes(s.notes)
      setEditGoesTo(s.editGoesTo || '')
      setNarrators(s.narrators || '')
      setLeftOffPage(s.leftOffPage || '')
      setLunchCost(s.lunchCost || '')
      setSocialTitle(s.socialTitle || '')
      setNotableTalent(s.notableTalent || '')
    } else {
      // Recall carries the delivery + narrator forward like the format
      // does — same project, same people. A calendar prefill wins.
      setEditGoesTo((v) => v || s.editGoesTo || '')
      setNarrators((v) => v || s.narrators || '')
      setSocialTitle((v) => v || s.socialTitle || '')
      setNotableTalent((v) => v || s.notableTalent || '')
    }
    setSeeded(true)
  }, [source.data, editing, seeded])

  // The title arrives pre-selected on a new session — typing replaces it.
  // Layout effect so focus lands BEFORE paint: the keyboard the tap opened
  // via primeKeyboard() transfers to this input, then the decoy retires.
  useLayoutEffect(() => {
    if (!editing) {
      titleRef.current?.focus()
      titleRef.current?.select()
      adoptKeyboard()
    }
  }, [editing])

  // Cold cache: the day count arrived after mount. Only replace the title
  // the form set itself — never something the user has typed — and keep it
  // selected so typing still replaces it.
  useEffect(() => {
    const day = dayTitle(project.data)
    if (editing || !day || label !== autoLabel.current || label === day) return
    autoLabel.current = day
    setLabel(day)
    requestAnimationFrame(() => titleRef.current?.select())
  }, [project.data, editing, label])

  const recalledLabel =
    !editing && fromId && source.data?.session
      ? source.data.session.label || fmtDay(source.data.session.date)
      : null

  // Whose "Day 2" is this? Multi-day projects all name their days the same
  // way, so the form says which project it belongs to — a food cost once
  // landed on the other project's Day 2 (Charles, 2026-08-05).
  const projectName = source.data?.session?.projectName || project.data?.project?.name || null

  function setGearField(i, field, value) {
    setGear((g) => g.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)))
  }

  // The mic locker stays open and each mic is a tick, so a session's mics
  // are picked in one go (Charles, 2026-10-09: "make more than 1 mic
  // selectable"). Ticking adds the mic's row; unticking takes its row away.
  // A second of the same mic is "Other…" with the name typed in.
  function toggleMic(name) {
    setGear((rows) => {
      const at = rows.map((r) => r.name).lastIndexOf(name)
      return at >= 0 ? rows.filter((_, i) => i !== at) : [...rows, { name, settings: '' }]
    })
  }
  function addOtherMic() {
    setGear((rows) => [...rows, { name: '', settings: '' }])
  }

  // "Other" + a description → offered as a permanent session type
  // (Charles, 2026-10-09), the same offer the Session Builder makes.
  const [makingType, setMakingType] = useState(false)
  async function makeTypePermanent() {
    const name = sessionTypeOther.trim()
    if (!name || makingType) return
    setMakingType(true)
    try {
      const r = await api('/log/session-types', { body: { name } })
      qc.setQueryData(['sl-session-types'], (old) => ({ ...(old || {}), sessionTypes: r.sessionTypes, canAdd: true }))
      qc.invalidateQueries({ queryKey: ['sa-status'] }) // the Session Builder's picker shares the list
      setSessionType(r.name)
      setSessionTypeOther('')
      toast(r.existed ? `“${r.name}” was already a session type` : `“${r.name}” is now a session type here and in the Session Builder`)
    } catch (err) {
      toast(err.message || 'Couldn’t add the session type', 'error')
    } finally {
      setMakingType(false)
    }
  }

  async function save(e) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    const cleanLabel = label.trim() === TITLE_PLACEHOLDER ? '' : label.trim()
    const body = {
      label: cleanLabel,
      sessionDate: date,
      room,
      sampleRate,
      bitDepth,
      gear: gear.filter((g) => g.name.trim()).map((g) => ({ name: g.name.trim(), settings: (g.settings || '').trim() })),
      chain: chain.split(/→|>|,/).map((x) => x.trim()).filter(Boolean),
      notes,
      editGoesTo: editGoesTo.trim(),
      narrators: narrators.trim(),
      leftOffPage: leftOffPage.trim(),
      sessionType,
      sessionTypeOther: sessionType === 'Other' ? sessionTypeOther.trim() : '',
      lunchCost: lunchCost.trim(),
      socialTitle: socialTitle.trim(),
      notableTalent: notableTalent.trim(),
    }
    try {
      let id = sessionId
      if (editing) {
        await api(`/log/sessions/${sessionId}`, { method: 'PATCH', body })
      } else {
        // With a project the session lands in it; without one it's standalone.
        const res = await api(projectId ? `/log/projects/${projectId}/sessions` : '/log/sessions', {
          body: { ...body, calendarTitle: calendarTitle.current, recalledFrom: fromId ? Number(fromId) : null },
        })
        id = res.id
      }
      // Photos picked during creation upload once the session exists.
      for (const p of photos) {
        try {
          await upload(`/log/sessions/${id}/media`, p.file)
        } catch (err) {
          toast(`“${p.file.name}” didn’t upload: ${err.message || 'error'}`, 'error')
        }
      }
      qc.invalidateQueries({ queryKey: ['sl-session', String(id)] })
      if (projectId) qc.invalidateQueries({ queryKey: ['sl-project', projectId] })
      qc.invalidateQueries({ queryKey: ['sl-projects'] })
      // A log made from the check page should clear its flag (and the red
      // dots) without waiting for the 5-minute cache.
      qc.invalidateQueries({ queryKey: ['sl-check'] })
      navigate(projectId ? `/log/${projectId}/s/${id}` : `/log/s/${id}`, { replace: true })
    } catch (err) {
      toast(err.message || 'Couldn’t save session', 'error')
      setBusy(false)
    }
  }

  const inputCls =
    'tap w-full border border-hairline bg-grouped px-3 py-2.5 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link'

  return (
    <div>
      <BackBar
        to={
          editing
            ? (projectId ? `/log/${projectId}/s/${sessionId}` : `/log/s/${sessionId}`)
            : (projectId ? `/log/${projectId}` : '/log')
        }
        label="Back"
      />

      <form ref={formRef} onSubmit={save}>
        <div className="mb-4 border-b border-hairline pb-3">
          {/* The heading is the title field — no separate label row. */}
          <input
            ref={titleRef}
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onFocus={(e) => { if (e.target.value === autoLabel.current) e.target.select() }}
            className="w-full border-0 bg-transparent p-0 font-serif text-2xl font-semibold text-ink focus:outline-none"
            aria-label="Session title"
            autoFocus={!editing}
          />
          {projectName && <div className="mt-1 text-sm text-muted">{projectName}</div>}
          {recalledLabel && (
            <div className="mt-1 text-sm text-muted">
              Setup recalled from <span className="font-medium text-ink">{recalledLabel}</span> — everything below stays editable.
            </div>
          )}
        </div>

        <div className="space-y-5">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} required />

          <section>
            <h2 className="section-header mb-2">Room</h2>
            <Segmented options={ROOMS} value={room} onChange={setRoom} />
          </section>

          <section>
            <h2 className="section-header mb-2">Session type</h2>
            <Segmented options={typeOptions} value={sessionType} onChange={setSessionType} />
            {sessionType === 'Other' && (
              <>
                <input
                  type="text"
                  placeholder="What kind of session?"
                  value={sessionTypeOther}
                  onChange={(e) => setSessionTypeOther(e.target.value)}
                  className={inputCls + ' mt-2'}
                />
                {typesQ.data?.canAdd && sessionTypeOther.trim() && (
                  <button
                    type="button"
                    onClick={makeTypePermanent}
                    disabled={makingType}
                    className="tap mt-1 flex items-center gap-1 text-sm font-medium text-link disabled:opacity-60"
                  >
                    <Pin size={14} /> Make “{sessionTypeOther.trim()}” a permanent session type
                  </button>
                )}
              </>
            )}
          </section>

          <section>
            <h2 className="section-header mb-2">Narrator name(s)</h2>
            <input
              type="text"
              placeholder="Jane Doe, John Smith"
              value={narrators}
              onChange={(e) => setNarrators(e.target.value)}
              className={inputCls}
            />
          </section>

          {/* Social media (Charles, 2026-09-01). Deliberately optional and
              deliberately blank by default: filling either field is what
              flags the session for the admin digest at /log/social. */}
          <section>
            <h2 className="section-header mb-2">Social media</h2>
            <p className="mb-2 text-xs text-faint">
              Only if there’s something worth posting about — leave blank otherwise.
            </p>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Book / show / film title"
                aria-label="Book, show or film title"
                value={socialTitle}
                onChange={(e) => setSocialTitle(e.target.value)}
                className={inputCls}
              />
              <input
                type="text"
                placeholder="Notable talent — actor, author, host…"
                aria-label="Notable talent"
                value={notableTalent}
                onChange={(e) => setNotableTalent(e.target.value)}
                className={inputCls}
              />
            </div>
          </section>

          <section>
            <h2 className="section-header mb-2">Edit goes to</h2>
            <input
              type="text"
              inputMode="email"
              list="edit-email-options"
              placeholder="name@example.com · ftp.example.com"
              value={editGoesTo}
              onChange={(e) => setEditGoesTo(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              className={inputCls}
            />
            <datalist id="edit-email-options">
              {(completions.data?.editGoesTo || []).map((addr) => (
                <option key={addr} value={addr} />
              ))}
            </datalist>
          </section>

          <section>
            <h2 className="section-header mb-2">Left off on page</h2>
            <input
              type="text"
              placeholder="142 · Ch 7, p. 90"
              value={leftOffPage}
              onChange={(e) => setLeftOffPage(e.target.value)}
              className={inputCls}
            />
          </section>

          <section>
            <h2 className="section-header mb-2">Lunch cost</h2>
            <input
              type="text"
              placeholder="$42.50 · Dig Inn"
              value={lunchCost}
              onChange={(e) => setLunchCost(e.target.value)}
              className={inputCls}
            />
          </section>

          <section>
            <h2 className="section-header mb-2">Format</h2>
            <div className="space-y-2">
              <Segmented options={SAMPLE_RATES} value={sampleRate} onChange={setSampleRate} />
              <Segmented options={BIT_DEPTHS} value={bitDepth} onChange={setBitDepth} />
            </div>
          </section>

          <section>
            <h2 className="section-header mb-2">Mic(s)</h2>
            <div className="space-y-2">
              {gear.map((g, i) => (
                <div key={i} className="flex gap-2">
                  <div className="min-w-0 flex-1 space-y-1">
                    <input
                      type="text"
                      placeholder="Mic"
                      value={g.name}
                      onChange={(e) => setGearField(i, 'name', e.target.value)}
                      className={inputCls}
                    />
                    <input
                      type="text"
                      placeholder="Settings / position — e.g. cardioid, pad −10, 8 in"
                      value={g.settings || ''}
                      onChange={(e) => setGearField(i, 'settings', e.target.value)}
                      className={inputCls + ' text-sm'}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setGear((rows) => rows.filter((_, idx) => idx !== i))}
                    className="tap self-start text-faint"
                    aria-label="Remove mic"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}

              {picking ? (
                <div className="card p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="section-header">Mic locker · pick all that apply</span>
                    <button type="button" onClick={() => setPicking(false)} className="tap text-sm font-medium text-link">
                      Done
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {MIC_PRESETS.map((m) => {
                      const on = gear.some((g) => g.name === m)
                      return (
                        <button
                          key={m}
                          type="button"
                          aria-pressed={on}
                          onClick={() => toggleMic(m)}
                          className={'tap flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors duration-200 ' + (on ? 'border-navy bg-navy text-white' : 'border-ink text-ink active:bg-grouped')}
                          style={{ borderWidth: '1.5px' }}
                        >
                          {on && <Check size={14} strokeWidth={2.5} />}
                          {m}
                        </button>
                      )
                    })}
                    <button
                      type="button"
                      onClick={addOtherMic}
                      className="tap rounded-full border border-hairline px-3 py-1.5 text-sm text-muted active:bg-grouped"
                    >
                      Other…
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setPicking(true)}
                  className="tap flex items-center gap-1.5 text-sm font-medium text-ink underline underline-offset-2"
                >
                  <Plus size={14} /> Add mic
                </button>
              )}
            </div>
          </section>

          <section>
            <h2 className="section-header mb-2">Signal chain</h2>
            <input
              type="text"
              placeholder="U87 → 1073 → 1176 → Pro Tools"
              value={chain}
              onChange={(e) => setChain(e.target.value)}
              className={inputCls}
            />
            <p className="mt-1 text-xs text-faint">Separate with → or commas.</p>
          </section>

          {!editing && (
            <section>
              <h2 className="section-header mb-2">Photos</h2>
              <input
                ref={photoRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || [])
                  e.target.value = ''
                  if (files.length) setPhotos((p) => [...p, ...files.map((file) => ({ file, url: URL.createObjectURL(file) }))])
                }}
              />
              {photos.length > 0 && (
                <div className="mb-2 grid grid-cols-3 gap-2">
                  {photos.map((p, i) => (
                    <div key={p.url} className="relative">
                      <img src={p.url} alt="" className="aspect-square w-full rounded-lg border border-hairline object-cover" />
                      <button
                        type="button"
                        onClick={() => setPhotos((rows) => rows.filter((_, idx) => idx !== i))}
                        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded bg-ink/80 text-white"
                        aria-label="Remove photo"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => photoRef.current?.click()}
                className="tap flex items-center gap-1.5 text-sm font-medium text-ink underline underline-offset-2"
              >
                <Camera size={14} /> Add photos
              </button>
              <p className="mt-1 text-xs text-faint">Uploads when the session is created; tag them on the next screen.</p>
            </section>
          )}

          <section>
            <h2 className="section-header mb-2">Notes — what changed today?</h2>
            <textarea
              rows={5}
              placeholder="Tunings, positions, settings, decisions…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputCls + ' resize-y'}
            />
          </section>

          <button
            type="submit"
            disabled={busy}
            className="tap w-full rounded-xl bg-navy px-4 py-3 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Create session'}
          </button>
        </div>
      </form>
    </div>
  )
}
